/**
 * How many seconds of upload bandwidth to keep in-flight at once.
 *
 * budget = CONCURRENCY_BUDGET_MULTIPLIER × speed (bytes/s)
 *
 * Example: speed = 10 MB/s, multiplier = 8 → budget = 80 MB in-flight at once.
 * With 25 MB chunks that fits ~3 concurrent chunks before the budget is exceeded.
 *
 * Raise this to allow more parallelism at the cost of higher stall timeout risk
 * on slow networks (more bytes in-flight = longer to drain if the connection drops).
 */
export const CONCURRENCY_BUDGET_MULTIPLIER = 8;

export const MIN_UPLOAD_CONCURRENCY = 1;
export const MAX_UPLOAD_CONCURRENCY = 5;
const BYTES_PER_MB = 1_000_000;
const UNKNOWN_SPEED_SMALL_UPLOAD_LIMIT_MB = 5;
export const UNKNOWN_SPEED_SMALL_UPLOAD_LIMIT = UNKNOWN_SPEED_SMALL_UPLOAD_LIMIT_MB * BYTES_PER_MB;

/** How many additional workers can be added before hitting MAX_UPLOAD_CONCURRENCY. */
const getAvailableWorkerSlots = (currentConcurrency: number) =>
  Math.max(0, MAX_UPLOAD_CONCURRENCY - currentConcurrency);

/**
 * When upload speed is unknown (first few seconds of a session), we still want
 * to ramp up immediately for small files rather than waiting for a speed sample.
 *
 * Counts how many of the next queued uploads are small enough to start right away,
 * stopping at the first one that exceeds UNKNOWN_SPEED_SMALL_UPLOAD_LIMIT.
 *
 * Example: queued = [2 MB, 3 MB, 10 MB, 1 MB], availableSlots = 4
 *   → returns 2 (stops at 10 MB)
 */
const getSmallQueuedUploadSlots = ({
  queuedUploadSizes,
  availableWorkerSlots,
}: {
  queuedUploadSizes: number[];
  availableWorkerSlots: number;
}) => {
  let addedSlots = 0;

  for (const uploadSize of queuedUploadSizes.slice(0, availableWorkerSlots)) {
    if (uploadSize > UNKNOWN_SPEED_SMALL_UPLOAD_LIMIT) {
      break;
    }

    addedSlots += 1;
  }

  return addedSlots;
};

/**
 * Returns the desired upload concurrency for the current network conditions.
 *
 * Concurrency changes by at most ±1 per tick to avoid large swings under
 * unstable network conditions.
 *
 * Decision tree:
 *
 * 1. Speed unknown (first ~200ms of a session):
 *    - If the next queued upload is small (≤ 5 MB): +1 worker.
 *    - Otherwise: hold current concurrency and wait for a speed sample.
 *
 * 2. Speed known, nothing in-flight yet: hold (avoids dividing by zero / false reads).
 *
 * 3. Speed known, inFlightBytes < budget: +1 worker (cap: MAX).
 *
 * 4. Speed known, inFlightBytes ≥ budget: -1 worker (floor: MIN).
 *
 * @param speed          Measured upload speed in bytes/s, or null if not yet known.
 * @param inFlightBytes  Total bytes currently being transferred (from activeChunks + small uploads).
 * @param currentConcurrency  Number of workers currently demanded.
 * @param queuedUploadSizes   File sizes of uploads waiting to start, in queue order.
 *
 * @example
 * // 10 MB/s, 25 MB in-flight, budget = 80 MB → under budget → +1 worker
 * getUploadConcurrencyForSpeed(10_000_000, 25_000_000, 2, [25_000_000]) // → 3
 *
 * @example
 * // speed unknown, next queued = 2 MB (small) → +1 worker
 * getUploadConcurrencyForSpeed(null, 0, 1, [2_000_000, 4_000_000, 10_000_000]) // → 2
 */
export const getUploadConcurrencyForSpeed = ({
  speed,
  inFlightBytes,
  currentConcurrency,
  queuedUploadSizes = [],
}: {
  speed: number | null;
  inFlightBytes: number;
  currentConcurrency: number;
  queuedUploadSizes?: number[];
}) => {
  const availableWorkerSlots = getAvailableWorkerSlots(currentConcurrency);

  if (!speed) {
    const hasSmallQueuedUpload = getSmallQueuedUploadSlots({ availableWorkerSlots, queuedUploadSizes }) > 0;

    if (hasSmallQueuedUpload) {
      return currentConcurrency + 1;
    }

    return currentConcurrency;
  }

  if (inFlightBytes === 0) {
    return currentConcurrency;
  }

  const budget = CONCURRENCY_BUDGET_MULTIPLIER * speed;

  if (inFlightBytes < budget) {
    return Math.min(MAX_UPLOAD_CONCURRENCY, currentConcurrency + 1);
  }

  return Math.max(MIN_UPLOAD_CONCURRENCY, currentConcurrency - 1);
};
